/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import CardComponent from "./CardComponent";
import {DEFAULT_HALL, EMAIL_LOGIN_REQUIRED, EMAIL_VERIFICATION_REQUIRED} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import React from "react";
import {CustomerType} from "./BookingCustomer/BookingCustomerForm";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import {filteredCountryList, PhoneCountry} from "utils/countryUtils";
import {ErrorOption, FieldPath} from "react-hook-form";
import {CompanyInputs} from "types/entities/specific/CompanyInputs";
import {ObjectSchema} from "yup";
import clsx from "clsx";
import CardLabel from "pages/Front/Booking/components/BookingCustomer/CardLabel";
import {useRequiredField} from "utils/bookingUtils";
import CompanyFormInput from "pages/Front/Booking/components/CompanyForm/CompanyFormInput";
import CompanyFormCheckbox from "pages/Front/Booking/components/CompanyForm/CompanyFormCheckbox";
import CompanyFormPhoneWithCountrySelect
    from "pages/Front/Booking/components/CompanyForm/CompanyFormPhoneWithCountrySelect";

interface CompanyProps {
    clearError: (name: FieldPath<CompanyInputs>) => void;
    origin: string;
    defaultValues: any;
    errors: any;
    register: any;
    control: any;
    setSelectedCustomerType: (customerType) => void;
    selectedCustomerType: CustomerType;
    setValue: (name, value) => void;
    setError: (name: FieldPath<CompanyInputs>, error: ErrorOption, options?: {shouldFocus: boolean}) => void;
    schema: ObjectSchema<any>;
    selectedCountry: PhoneCountry;
    setSelectedCountry: (val: PhoneCountry) => void;
}

const CompanyForm = (props: CompanyProps) => {
    const {t} = useTranslation();
    const hall = useTypedSelector(state => state.hallState.hall);
    const companyTypes = [
        {id: "private", type: CustomerType.Private},
        {id: "business", type: CustomerType.Business},
    ];
    const setSelectedCustomerTypeHandler = (customerType: CustomerType) => {
        props.setSelectedCustomerType(customerType);
    };
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const emailVerified = props.origin == "gutschein" ? useTypedSelector(state => state.bookingState.bookingProcess.emailVerified) : true;
    const loginVerified = EMAIL_LOGIN_REQUIRED ? useTypedSelector(state => state.bookingState.bookingProcess.emailLogin) : true;
    const hideReservationNotes = true;
    // DEFAULT_HALL === HallCode.Zwolle ? false : selectedPackage && selectedPackage.category === PackageCategory.General;
    const requiredField = useRequiredField(props.schema);

    return (
        <div className={clsx("basis-[50%] flex-shrink-1 flex-grow-1 flex flex-col space-y-6", !emailVerified && "grayscale")}>
            {!EMAIL_VERIFICATION_REQUIRED && !EMAIL_LOGIN_REQUIRED && (
                <CardComponent>
                    <div className="col-span-2 sm:col-span-2 space-y-4 mb-2">
                        <CompanyFormInput
                            disabled={!emailVerified && !loginVerified}
                            errors={props.errors}
                            iconName={"email"}
                            label={"Email"}
                            name={"email"}
                            register={props.register}
                            required={requiredField.testField("email")}
                        />
                    </div>
                </CardComponent>
            )}

            <CardComponent>
                <div className={"CompanySwitcher col-span-2 flex flex-col justify-between items-center mb-2"}>
                    <div className={"CompanySwitcherInput flex flex-col w-full"}>
                        <CardLabel title={t("Customer information")} />
                        <p className="text-sm leading-5 text-gray-700 hidden">{t("Would you like to register as a business company?")}</p>
                        <fieldset className="my-4">
                            <legend className="sr-only">{t("Company type")}</legend>
                            <div className="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
                                {companyTypes.map(companyType => (
                                    <div key={companyType.id} className="flex items-center">
                                        <input
                                            {...props.register("company_type")}
                                            checked={companyType.type === props.selectedCustomerType}
                                            className="checked:bg-cta-800 active:ring-2 active:ring-cta-800 active:ring-offset-2 rounded-full h-4 w-4 text-contrast-800"
                                            // defaultChecked={companyType.type === CustomerType.Private}
                                            disabled={!emailVerified}
                                            id={`company-${companyType.type}`}
                                            type="radio"
                                            value={companyType.type === CustomerType.Business ? "true" : "false"}
                                            onChange={() => setSelectedCustomerTypeHandler(companyType.type)}
                                        />
                                        <label className="ml-3 block text-sm font-medium text-gray-800" htmlFor={`company-${companyType.type}`}>
                                            {t(companyType.type)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </fieldset>
                    </div>
                </div>
                {props.selectedCustomerType === CustomerType.Business && (
                    <div className="col-span-2 sm:col-span-2 space-y-4 mb-2">
                        <CompanyFormInput
                            disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                            errors={props.errors}
                            iconName={"company_name"}
                            label={"Company name"}
                            name={"company_name"}
                            register={props.register}
                            required={props.selectedCustomerType === CustomerType.Business}
                        />

                        {DEFAULT_HALL === HallCode.Zwolle && (
                            <CompanyFormInput
                                disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                                errors={props.errors}
                                iconName={"cin"}
                                label={"Company number ID"}
                                name={"cin"}
                                register={props.register}
                                required={requiredField.testField("cin")}
                            />
                        )}
                        <CompanyFormInput
                            disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                            errors={props.errors}
                            iconName={"street_address"}
                            label={"Street address"}
                            name={"street_address"}
                            register={props.register}
                            required={requiredField.testField("street_address")}
                        />
                        <CompanyFormInput
                            disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                            errors={props.errors}
                            iconName={"postal_code"}
                            label={"Postal code"}
                            name={"postal_code"}
                            register={props.register}
                            required={requiredField.testField("postal_code")}
                        />
                        <CompanyFormInput
                            disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                            errors={props.errors}
                            iconName={"city"}
                            label={"City"}
                            name={"city"}
                            register={props.register}
                            required={requiredField.testField("city")}
                        />
                        <br />
                        <h3 className={"font-semibold"}>{t("Contact person")}</h3>
                    </div>
                )}

                <div className="col-span-2 sm:col-span-2 space-y-4">
                    <CompanyFormInput
                        disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                        errors={props.errors}
                        iconName={"first_name"}
                        label={"First name"}
                        name={"first_name"}
                        register={props.register}
                        required={requiredField.testField("first_name")}
                    />
                    <CompanyFormInput
                        disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                        errors={props.errors}
                        iconName={"last_name"}
                        label={"Last name"}
                        name={"last_name"}
                        register={props.register}
                        required={requiredField.testField("last_name")}
                    />
                    <CompanyFormPhoneWithCountrySelect
                        defaultCountry={props.selectedCountry?.textCode}
                        defaultValue={props.defaultValues.phone}
                        disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                        errors={props.errors}
                        label={t("Phone")}
                        name={"phone"}
                        options={filteredCountryList([
                            "AT",
                            "BE",
                            "BG",
                            "HR",
                            "CY",
                            "CZ",
                            "DK",
                            "EE",
                            "FI",
                            "FR",
                            "DE",
                            "GR",
                            "HU",
                            "IE",
                            "IT",
                            "LV",
                            "LI",
                            "LT",
                            "LU",
                            "MT",
                            "NL",
                            "PL",
                            "PT",
                            "RO",
                            "SK",
                            "SI",
                            "ES",
                            "SE",
                            "GB",
                            "NO",
                            "SE",
                            "CH",
                            "US",
                            "AD",
                            "ES",
                            "ES",
                            "FR",
                            "FO",
                            "GI",
                            "IM",
                            "AX",
                        ])}
                        register={props.register}
                        required={requiredField.testField("phone")}
                        setSelectedCountry={props.setSelectedCountry}
                        setValue={value => props.setValue("phone", value)}
                    />
                    {DEFAULT_HALL !== HallCode.Zwolle && props.selectedCustomerType === CustomerType.Private && (
                        <>
                            <CompanyFormInput
                                disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                                errors={props.errors}
                                iconName={"street_address"}
                                label={"Street address"}
                                name={"street_address"}
                                register={props.register}
                                required={requiredField.testField("street_address")}
                            />
                            <CompanyFormInput
                                disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                                errors={props.errors}
                                iconName={"postal_code"}
                                label={"Postal code"}
                                name={"postal_code"}
                                register={props.register}
                                required={requiredField.testField("postal_code")}
                            />
                            <CompanyFormInput
                                disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                                errors={props.errors}
                                iconName={"city"}
                                label={"City"}
                                name={"city"}
                                register={props.register}
                                required={requiredField.testField("city")}
                            />
                        </>
                    )}
                </div>
            </CardComponent>

            {!hideReservationNotes && (
                <CardComponent>
                    <div className={"BookingReservationNoteTitle mb-2"}>
                        <h3 className={"font-bold"}>{t("Send us a note")}</h3>
                    </div>
                    <textarea
                        className={"p-2 w-full border-gray-500 border rounded text-base font-medium min-h-[120px]"}
                        {...props.register("reservation_note")}
                    />
                </CardComponent>
            )}

            <CardComponent className={"!p-0"}>
                <CompanyFormCheckbox
                    disabled={props.origin === "gutschein" ? !emailVerified : !emailVerified || !loginVerified}
                    errors={props.errors}
                    name={DEFAULT_HALL === HallCode.Spreitenbach || DEFAULT_HALL === HallCode.Winterthur ? "tos_verifier" : "tos"}
                    register={props.register}
                    required={true}
                >
                    <span className={"select-none"}>{t("I accept the no refund policy, general terms and conditions and privacy policy of the booking")}</span>
                    <a
                        className={"LinkToTos font-bold text-xs w-auto inline-flex"}
                        href={DEFAULT_HALL === HallCode.Zwolle ? "https://www.kobalt.nl/algemene-voorwaarden" : hall ? hall.condition_terms_link : "/terms"}
                        rel={"noreferrer"}
                        target={"_blank"}
                        title={t("Terms and conditions")}
                    >
                        ({t("Read terms & conditions")})
                    </a>
                </CompanyFormCheckbox>
            </CardComponent>

            <CardComponent className={"text-sm"}>
                <span>
                    {t(
                        "ArboPark may process and use the data collected during the conclusion of the contract to fulfill the obligations arising from the purchase contract and for marketing purposes."
                    )}
                    {t("This consent can be revoked at any time in writing or by email")}
                </span>
            </CardComponent>
        </div>
    );
};

export default CompanyForm;
